import axios from "axios";
import clsx from "clsx";
import React, { useEffect, useState, forwardRef } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useHighlight } from "../../hooks/useHighlight";

import { useMatomo } from "@datapunt/matomo-tracker-react";

/** APi */
import APIs from "../../APIs";

/** Material-UI */
import {
    alpha,
    FormControlLabel,
    Grid,
    IconButton,
    List,
    ListItem,
    Typography,
    useMediaQuery,
    TextField,
    MenuItem,
    Checkbox,
    Box,
} from "@material-ui/core";
import { UnfoldLess, UnfoldMore, ExpandMore, Check } from "@material-ui/icons";
import Alert from "@material-ui/lab/Alert";
import { makeStyles } from "@material-ui/styles";

/** UI */
import { useDocTitle } from "../../components/_common/DocTitle";
import Preloader from "../../components/_common/Preloader";
import Paginator from "../../components/Paginator";
import Search from "../../components/Search";
import AktyAccordion from "./AktyAccordion";
import AktyCard from "./AktyCard";
import ObjectCard from "./ObjectCard";
import ObjectsAccordion from "./ObjectsAccordion";

const regex = /(<([^>]+)>)|\t|\n|&nbsp;/gi;

const ALL_EXPANDED = process.env.REACT_APP_ALL_EXPANDED_CEC
    ? JSON.parse(process.env.REACT_APP_ALL_EXPANDED_CEC)
    : process.env.REACT_APP_ALL_EXPANDED
    ? JSON.parse(process.env.REACT_APP_ALL_EXPANDED)
    : false;

const BORDERED_LAYOUT = process.env.REACT_APP_BORDERED_LAYOUT
    ? JSON.parse(process.env.REACT_APP_BORDERED_LAYOUT)
    : false;

const DropDownIcon = forwardRef((props, ref) => (
    <IconButton ref={ref} {...props} size={"small"} component={"div"}>
        <ExpandMore color={"primary"} fontSize={"small"} />
    </IconButton>
));

const useStyles = makeStyles((theme) => ({
    bigTitle: {
        margin: theme.spacing(0, 4, 4, 4),
        textAlign: "center",
        justifyContent: "center",
        [theme.breakpoints.down("sm")]: {
            fontSize: "1.125rem",
            fontWeight: theme.typography.fontWeightMedium,
        },
    },
    alphabet: {
        display: "flex",
        justifyContent: "space-between",
        overflow: "hidden",
    },
    listOverlay: {
        content: '""',
        position: "absolute",
        width: "100%",
        height: 1,
        bottom: -1,
        boxShadow: `0 1px 56px 64px ${theme.palette.background.paper}`,
        zIndex: 1,
    },
    listRoot: {
        maxHeight: "calc(100vh - 0px)",
        overflow: "hidden",
        overflowY: "scroll",
        marginTop: theme.spacing(4),
        paddingBottom: "100vh",
        position: "relative",
        scrollBehavior: "smooth",
    },
    listSection: {
        backgroundColor: "inherit",
    },
    ul: {
        backgroundColor: "inherit",
        padding: 0,
    },
    listSubheaderSticky: {
        backgroundColor: "rgb(234 237 244)",
        color: theme.palette.primary.contrastText,
        padding: theme.spacing(2),
        width: "100%",
    },
    listItemSelected: {
        backgroundColor: `${alpha(theme.palette.primary.main, 0.0675)}!important`,
    },
    alphabetChip: {
        backgroundColor: alpha(theme.palette.primary.light, 0.5),
        color: theme.palette.primary.contrastText,
        "&:hover": {
            backgroundColor: theme.palette.primary.main,
        },
        "&:focus": {
            backgroundColor: theme.palette.primary.main,
        },
    },
    alphabetChipAcvtive: {
        backgroundColor: theme.palette.primary.main,
    },
    alphabetAvatar: {
        backgroundColor: theme.palette.primary.main,
    },
    searchFixed: {
        position: "fixed",
        zIndex: 1,
        width: "100%",
        backgroundColor: "#fff",
        maxWidth: 1186,
        top: 52,
    },
    listItemAvatarAvatar: {
        backgroundColor: alpha(theme.palette.primary.main, 0.25),
    },
    formControl: {
        marginTop: theme.spacing(2),
        minWidth: "100%",
    },
    listItemLI: {
        display: "flex",
        alignItems: "flex-start",
        flexWrap: "wrap",
        transition: "all 0.1s linear",
        border: BORDERED_LAYOUT ? "1px solid" : "none",
        borderColor: BORDERED_LAYOUT ? alpha(theme.palette.primary.main, 0.25) : "inherit",
        borderRadius: BORDERED_LAYOUT ? 20 : 0,
        overflow: "hidden",
        marginBottom: theme.spacing(5),
        padding: theme.spacing(0, 0, 1, 0),
    },
    ReactVirtualizedList: {
        "&::-webkit-scrollbar": {},
        [theme.breakpoints.down("sm")]: {
            width: "100vw",
        },
    },
    searchWrapper: {
        "&, & > form": {
            display: "flex",
            alignItems: "center",
        },
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
        },
    },
    btnReport: {
        margin: 0,
        height: 48,
        minWidth: 241,
        fontSize: "clamp(12px, 1vw, 14px)",
        "& > span": {
            marginTop: 2,
        },
        "&:focus-visible": {
            outline: "#000 auto 2px",
            outlineOffset: 3,
        },
        [theme.breakpoints.down("sm")]: {
            margin: theme.spacing(0, 0, 0, 0),
            width: "100%",
            height: 36,
            paddingTop: 4,
        },
    },
    searchForm: {
        width: "100%",
        minHeight: 80,
        display: "flex",
        gap: 16,
    },
    btnSearch: {
        margin: 0,
        height: 48,
        minWidth: 153,
        "& > span": {
            marginTop: 2,
        },
        "&:focus-visible": {
            outline: "#000 auto 2px",
            outlineOffset: 3,
        },
        [theme.breakpoints.down("sm")]: {
            margin: theme.spacing(0, 0, 4, 0),
        },
    },
    alert: {
        marginTop: theme.spacing(6),
        "&, & *": {
            color: "#621B16",
            fontWeight: "bolder",
        },
    },
    paginationList: {
        opacity: 1,
        "& li": {
            fontSize: "0.875rem",
            fontWeight: 400,
            lineHeight: 1.43,
        },
    },
    paginationRoot: {
        "& input": {
            opacity: 0,
            color: "#000",
        },
    },
    paginationSelect: {
        fontSize: "0.875rem",
        fontWeight: 400,
        lineHeight: 1.43,
        paddingBottom: 6,
    },
    paginationSelectIcon: {
        top: "calc(50% - 13px)",
        color: "#000",
    },
    visuallyhidden: {
        border: 0,
        clip: "rect(0 0 0 0)",
        height: 1,
        margin: -1,
        overflow: "hidden",
        padding: 0,
        position: "absolute",
        width: 1,
    },
    toolsWrapper: {
        marginTop: 16,
        padding: theme.spacing(0, 3, 0, 5.5),
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        // borderBottom: "1px solid #00000011",
        // position: "sticky",
        // top: 119,
        // zIndex: 1,
        // backgroundColor: "#fff",
    },
    formControlLabel: {
        fontSize: "clamp(12px, 1vw, 1rem)",
    },
    xsOnly: {
        display: "none",
        [theme.breakpoints.down("xs")]: {
            display: "block",
            width: "100%",
            backgroundColor: "#00000022",
        },
    },
    cechaName: {
        color: "inherit",
        textDecoration: "inherit",
        fontSize: "clamp(14px, 2vw, 1.25rem)",
        fontWeight: 600,
        display: "block",
        padding: "8px 16px",
        // backgroundColor: "#FAFAFA",
        textWrap: "pretty",
        [theme.breakpoints.down("sm")]: {
            padding: "8px 8px",
        },
    },
    textField: {
        "& label": {
            fontSize: "clamp(13px, 1vw, 1rem)",
        },
        "& fieldset": {
            [theme.breakpoints.down("sm")]: {
                top: -7,
            },
        },
        "& legend": {
            fontSize: "0.835em",
            [theme.breakpoints.down("sm")]: {
                fontSize: "0.6em",
            },
        },
        "& button": {
            top: "calc(50% - 14px)",
        },
        "& input": {
            left: -1999,
        },
    },
    input: {
        fontSize: "clamp(13px, 1vw, 1rem)",
        paddingTop: 15,
        paddingBottom: 14,
        paddingLeft: 14,
        "& .check": {
            display: "none",
        },
    },
    label: {
        fontSize: "clamp(13px, 1vw, 1rem)",
        display: "flex",
        width: "100%",
        justifyContent: "space-between",
    },
    inputAdornmentEnd: {},
    datePickerButtonRoot: {
        "& svg": {
            color: theme.palette.primary.main,
        },
    },
    adornedEnd: {
        paddingRight: 0,
    },
    selectMenuPaper: {
        opacity: 1,
        '& li[class*="MuiListItem-selected"]': {
            backgroundColor: "transparent",
            color: theme.palette.primary.main,
            fontWeight: 500,
        },
    },
}));

const RSICECHY_Page = (props) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { highlight } = useHighlight();
    const { trackPageView } = useMatomo();
    const history = useHistory();

    useDocTitle(t("pages.featuresPageTitle"));

    const isMobile = useMediaQuery("(max-width: 349.95px)");

    const [apiWait, setApiWait] = useState(true);
    const [LOADING, setLOADING] = useState(false);
    const [FULL_DATA, setFULL_DATA] = useState([]);
    const [SEARCH_ERROR, setSEARCH_ERROR] = useState(false);
    const [SEARCH, setSEARCH] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [count, setCount] = useState(0);
    const [EXPANDED, setEXPANDED] = useState([]);
    const [PRIMITIVES, setPRIMITIVES] = useState([]);
    const [QUERY, setQUERY] = useState("");
    const [SORT, setSORT] = useState("nazwa_cechy");

    /** Process url query */
    useEffect(() => {
        const currentQuery = Object.fromEntries(new URLSearchParams(window.location.search));

        if (!Object.keys(currentQuery)?.length) {
            setApiWait(false);
            return;
        }

        if (
            Number(currentQuery?.page) !== page ||
            Number(currentQuery?.limit) !== rowsPerPage ||
            currentQuery?.search !== encodeURI(SEARCH)
        ) {
            if (Number(currentQuery?.limit) !== rowsPerPage) {
                setRowsPerPage(Number(currentQuery?.limit));
            }

            if (currentQuery?.search !== encodeURI(SEARCH)) {
                setSEARCH(currentQuery?.search ? decodeURI(currentQuery?.search) : "");
            }

            if (Number(currentQuery?.page) !== page) {
                setPage(Number(currentQuery?.page) - 1);
            }

            setApiWait(false);
        } else {
            setApiWait(false);
        }
        // eslint-disable-next-line
    }, []);

    /** get api data */
    useEffect(() => {
        if (apiWait) return;
        setSEARCH_ERROR(false);
        getData({ page });
        return;
        // eslint-disable-next-line
    }, [SEARCH, page, rowsPerPage, apiWait]);

    /** track page view */
    useEffect(() => {
        trackPageView();
        // eslint-disable-next-line
    }, []);

    /** Handlers */
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleOnSubmitSearch = (newSearchText) => {
        setPage(0);
        setSEARCH(newSearchText);
    };

    const handleToggleExpand = (event) => {
        event.preventDefault();
        event.stopPropagation();
        const all = PRIMITIVES;

        let panels = EXPANDED?.length ? (EXPANDED.length === all?.length ? [] : all) : all;

        setEXPANDED(panels);
    };

    const handleOnExpand = (panel) => {
        const panels = EXPANDED?.includes(panel) ? EXPANDED?.filter((e) => e !== panel) : [...EXPANDED, panel];

        setEXPANDED(panels);
    };

    const handleSort = async (event) => {
        setSORT(event.target.value);
        const ordering = event.target.value;

        const currentQuery = Object.fromEntries(new URLSearchParams(window.location.search));
        const newQuery = {
            ...currentQuery,
            ordering,
        };

        setApiWait(true);

        await Promise.all([
            // setPage(0),
            newQuery && setQUERY(newQuery),
        ]).then((res) => {
            setApiWait(false);
            setLOADING(false);
        });
    };

    const getData = ({ page }) => {
        // let apiURL = `${APIs.dataURL.rsicechy.split("?")[0]}?search=${SEARCH}&limit=${rowsPerPage}&page=${page + 1}`;
        let currentQuery = Object.fromEntries(new URLSearchParams(window.location.search));

        const newQuery = new URLSearchParams(
            QUERY || { ...currentQuery, limit: rowsPerPage, page: page + 1, search: SEARCH || "" }
        ).toString();

        let apiURL = `${APIs.dataURL.rsicechy.split("?")[0]}?${newQuery}`;

        const newPath = `${history.location.pathname}?${newQuery}${history.location.hash}`;

        setTimeout(() => {
            history.push(newPath, { shallow: true });
        }, 100);

        setLOADING(true);

        axios
            .get(apiURL)
            .then((result) => {
                setCount(result.data.count);
                if (SEARCH && !result?.data?.count) {
                    setSEARCH_ERROR(
                        t('Brak wyników wyszukiwania dla frazy "{{SEARCH}}"{{dot}}', {
                            SEARCH,
                            dot: ".",
                        })
                    );
                }

                const apiData = result.data.results.map((result) => {
                    result.obiekty.forEach((object, index) => {
                        result.obiekty[index].nazwa_obiektu = result.obiekty[index].nazwa_obiektu.replace(regex, "");

                        result.obiekty[index].systemy.forEach((system, i) => {
                            result.obiekty[index].systemy[i].nazwa_systemu = result.obiekty[index].systemy[
                                i
                            ].nazwa_systemu.replace(regex, "");
                        });

                        return result;
                    });

                    return {
                        ...result,
                        nazwa_cechy: result.nazwa_cechy.replace(regex, ""),
                    };
                });

                const objekty = apiData?.flatMap((d) => {
                    // return d.obiekty?.map((o) => ({ id_cechy: o.id_cechy, expand_id: `obj-${o.id_cechy}`, ...o }));
                    return d.obiekty?.map((o) => `obj-${d.id_cechy}`);
                });
                const aktyprawne = apiData?.flatMap((d) => {
                    // return d.aktyprawne?.map((a) => ({ id_cechy: a.id_cechy, expand_id: `akt-${a.id_cechy}`, ...a }));
                    return d.aktyprawne?.map((a) => `akt-${d.id_cechy}`);
                });

                const primitives = [...objekty, ...aktyprawne];

                setPRIMITIVES(primitives);

                if (ALL_EXPANDED) setEXPANDED(primitives);

                setFULL_DATA(apiData);
            })

            .catch((error) => {
                if (SEARCH) {
                    setSEARCH_ERROR(
                        t('Brak wyników wyszukiwania dla frazy "{{SEARCH}}"{{dot}}<br/>{{apiError}}', {
                            SEARCH,
                            dot: ".",
                            apiError: error.response.data.detail ? `<em>${error.response.data.detail}</em>` : "",
                        })
                    );
                } else {
                    setSEARCH_ERROR(error.response?.data?.detail || t("Nieznany błąd"));
                }
            })
            .finally(() => {
                setLOADING(false);
                setQUERY("");
            });
    };

    const optionsSort = [
        { label: t("Nazwa od A do Z"), value: "nazwa_cechy", default: true },
        { label: t("Nazwa od Z do A"), value: "-nazwa_cechy" },
        { label: t("Data aktualizacji rosnąco"), value: "DA", date: true },
        { label: t("Data aktualizacji malejąco"), value: "DD", date: true },
    ];

    return (
        <>
            <Typography
                variant="h4"
                className={classes.bigTitle}
                color="primary"
                component={"h2"}
                id={"page-content"}
                tabIndex={0}
            >
                {t("pages.features")}
            </Typography>

            <Grid container>
                <Grid item xs={12} className={classes.searchWrapper}>
                    <Search
                        label={t("common.RSICECHYsearchFeature")}
                        onSubmit={(newSearchText) => handleOnSubmitSearch(newSearchText)}
                        error={SEARCH_ERROR}
                        search={SEARCH}
                    />
                </Grid>

                <Grid item xs={12} style={{ marginTop: 16 }}>
                    &nbsp;
                </Grid>

                <Grid item xs={12} style={{ position: "relative" /*overflow: "hidden"*/ }} id="aGrid">
                    <div className={classes.listOverlay0} />

                    {!!SEARCH_ERROR && (
                        <Alert severity="error" className={classes.alert}>
                            <span dangerouslySetInnerHTML={{ __html: SEARCH_ERROR }} />
                        </Alert>
                    )}

                    {!!LOADING && (
                        <>
                            <Preloader type="linear" />
                            <Preloader type="linear" />
                            <Preloader type="linear" />
                        </>
                    )}

                    {!!FULL_DATA?.length && !LOADING && (
                        <Paginator
                            component="div"
                            count={count}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            queryParams={{ limit: rowsPerPage, page: page + 1, search: SEARCH }}
                            componentTopBefore={
                                <TextField
                                    id="cechy-sort"
                                    select
                                    label={t("Sortowanie")}
                                    value={SORT}
                                    onChange={handleSort}
                                    variant="outlined"
                                    fullWidth={!!isMobile}
                                    SelectProps={{
                                        IconComponent: DropDownIcon,
                                        MenuProps: {
                                            getContentAnchorEl: null,
                                            anchorOrigin: { vertical: "bottom", horizontal: "center" },
                                            transformOrigin: { vertical: "top", horizontal: "center" },
                                            PaperProps: {
                                                className: classes.selectMenuPaper,
                                            },
                                        },
                                        id: "cechy-sort-select",
                                    }}
                                    size={isMobile ? "small" : "medium"}
                                    classes={{ root: classes.textField }}
                                    inputProps={{
                                        className: classes.input,
                                    }}
                                    style={{
                                        order: isMobile ? 1 : 2,
                                        width: isMobile ? undefined : "clamp(280px, 20vw, 328px)",
                                    }}
                                    ref={(ref) => {
                                        if (ref) {
                                            const inputEl = ref.querySelector("input");

                                            if (inputEl) {
                                                setTimeout(() => {
                                                    inputEl.style.color = "#000";
                                                    inputEl.style.backgroundColor = "#FFF";
                                                    inputEl.style.opacity = 1;
                                                    inputEl.id = "cechy-sort";
                                                }, 500);
                                            }
                                        }
                                    }}
                                >
                                    {optionsSort.map((option) => {
                                        const _hasDateField = false; // data?.every((d) => d.hasOwnProperty("data_aktualizacji"));

                                        return option.date && !_hasDateField ? null : (
                                            <MenuItem key={option.value} value={option.value}>
                                                <Box
                                                    // component={option.default ? "em" : "span"}
                                                    className={classes.label}
                                                >
                                                    {option.label}
                                                    {option.value === SORT && (
                                                        <Check className="check" color="primary" />
                                                    )}
                                                </Box>
                                            </MenuItem>
                                        );
                                    })}
                                </TextField>
                            }
                        >
                            <div className={classes.toolsWrapper}>
                                <span />

                                <FormControlLabel
                                    htmlFor="expand-cechy-btn"
                                    control={
                                        <>
                                            <IconButton onClick={handleToggleExpand} component={"span"}>
                                                {EXPANDED?.length === PRIMITIVES?.length ? (
                                                    <UnfoldLess fontSize={isMobile ? "small" : "medium"} />
                                                ) : (
                                                    <UnfoldMore fontSize={isMobile ? "small" : "medium"} />
                                                )}
                                            </IconButton>
                                            <Checkbox id="expand-cechy-btn" inert style={{ display: "none" }} />
                                        </>
                                    }
                                    label={
                                        EXPANDED?.length === PRIMITIVES?.length
                                            ? t("Zwiń wszystkie")
                                            : t("Rozwiń wszystkie")
                                    }
                                    labelPlacement="start"
                                    classes={{ label: classes.formControlLabel }}
                                />
                            </div>

                            <List className={classes.listRoot0} id="aList" disablePadding>
                                {FULL_DATA?.map((data, index) => {
                                    // console.log(data)
                                    return (
                                        <React.Fragment key={data.id_cechy}>
                                            <ListItem
                                                dense={true}
                                                className={clsx(classes.listItemLI)}
                                                key={data.id_cechy}
                                                id={`item-${data?.id_cechy}`}
                                            >
                                                <List style={{ width: "100%", padding: 0 }}>
                                                    <ListItem className={classes.cechaName} disableGutters>
                                                        {/* {data?.nazwa_cechy} */}
                                                        {highlight({
                                                            text: data?.nazwa_cechy,
                                                            search: SEARCH,
                                                            classes,
                                                        })}
                                                        {/* </ListItem> */}

                                                        {!!data?.obiekty.length && data?.obiekty.length === 0 ? (
                                                            <ObjectCard obiekt={data?.obiekty[0]} />
                                                        ) : (
                                                            <ObjectsAccordion
                                                                id_cechy={data?.id_cechy}
                                                                obiekty={data?.obiekty}
                                                                expanded={EXPANDED}
                                                                onExpand={(panel) => handleOnExpand(panel)}
                                                                isMobile={isMobile}
                                                            />
                                                        )}
                                                        {!!data?.aktyprawne.length ? (
                                                            data?.aktyprawne.length === 0 ? (
                                                                <AktyCard akt={data?.aktyprawne[0]} />
                                                            ) : (
                                                                <AktyAccordion
                                                                    id_cechy={data?.id_cechy}
                                                                    akty={data?.aktyprawne}
                                                                    cecha={data?.nazwa_cechy}
                                                                    expanded={EXPANDED}
                                                                    onExpand={(panel) => handleOnExpand(panel)}
                                                                    isMobile={isMobile}
                                                                />
                                                            )
                                                        ) : (
                                                            <></>
                                                        )}
                                                    </ListItem>
                                                </List>
                                            </ListItem>
                                        </React.Fragment>
                                    );
                                })}
                            </List>
                        </Paginator>
                    )}
                </Grid>
            </Grid>
        </>
    );
};

export default RSICECHY_Page;
